import { defineComponent as _defineComponent } from 'vue'
import Client from '@/models/Client';
import ClientApi from '@/modules/client/services/ClientApi';
import ClientRodo from '@/components/ClientRodo.vue';
import {
  Agreements,
  AgreementViews,
  ClientAgreements,
  IAgreementItemViewModel
} from '@/modules/client/views/client-agreements/Agreements';
import {clone} from '@/services/utils/BasicUtils';
import MultiformApi from '@/modules/multiForm/services/MultiformApi';
import {ProductType} from '@/commons/enums/ProductType';
import { cloneDeep } from 'lodash-es';
import ClientOwnerInfoCard from '@/modules/client/views/ClientOwnerInfoCard.vue';
import User from '@/models/user/User';
import AuthService from '@/modules/user/AuthService';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import {ComponentPublicInstance, computed, onMounted, ref, watch} from 'vue';
import useGeoAreas from '@/composables/useGeoAreas';
import {useTour} from '@/composables/useTour';
import {useInstance} from '@/composables/useInstance';
import EnvironmentService from '@/env/EnvironmentService';
import ValidationApi from '@/commons/services/ValidationApi';


export default /*#__PURE__*/_defineComponent({
  __name: 'EditClientForm',
  props: {
    value: null,
    delegateForm: { type: Boolean },
    delegationGoal: null,
    isEdit: { type: Boolean, default: false },
    peselRequired: { type: Boolean, default: false },
    dirty: { type: Boolean, default: false },
    fieldsApiValidation: null
  },
  emits: ["update:fieldsApiValidation", "update:dirty", "update:agreement"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:fieldsApiValidation', event: any): void,
  (e: 'update:dirty', event: any): void,
  (e: 'update:agreement', event: any): void,
  (e: 'update:agreement'): void,
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: Client, delegateForm?: boolean, delegationGoal?: ProductType, isEdit: boolean, peselRequired: boolean, dirty: boolean, fieldsApiValidation?: Record<string, string | null | boolean> }



const { $refs, $errorHandler, $user, $modalService, $snackbarService, $env, } = useInstance();
const geoAreas = useGeoAreas();
const keysChanged = ref<(keyof ClientAgreements)[]>([]);
const gpdrAgreement = EnvironmentService.Environment.getGPDRDomainAgreementsWithUrl();

const clientAgreements = ref<Agreements>(props.delegationGoal ? new Agreements() : cloneDeep(props.value.agreements));

const clientCopy = ref<Nullable<any>>(null);
const existingClient = ref<Nullable<Client>>(null);
const clientOwner = ref<Nullable<User>>(null);
const checkAll = ref<Nullable<boolean>>(null);



const fp_phone = ref(null)
const phoneErrorMessage = ref<String>('');

async function validatePhone(): Promise<void> {
  const isValid = (fp_phone.value as any).validate();

  if (!isValid) {
    return;
  }

  try {
    const isRealNumber = await ValidationApi.phone(props.value.phone as string);
    phoneErrorMessage.value = '';
    if (!isRealNumber) {
      $snackbarService.openErrorSnackbar(I18NGetter().fpComponents.PHONE_CUSTOM_RULES_MSG);
      phoneErrorMessage.value = I18NGetter().fpComponents.PHONE_CUSTOM_RULES_MSG;
    }
  } catch (e) {
    $errorHandler(e, I18NGetter().fpComponents.PHONE_CUSTOM_RULES_MSG);
    phoneErrorMessage.value = I18NGetter().fpComponents.PHONE_CUSTOM_RULES_MSG;
  }
}

const findIfParamExists = async(key: keyof Client, value: Nullable<string>) => {
  if (value && (clientCopy.value === null || clientCopy.value[key] !== value)) {
    const field = ($refs[`fp_${key}`] as any);
    if (props.fieldsApiValidation) {
      props.fieldsApiValidation[key] = null;
    }
    const validField: boolean = field.valid || field?.validate();
    if (value.length && validField) {
      const properties: Record<string, string | boolean> = {};
      properties[key] = value;
      const validationKeys = ['email', 'phone',];
      if (validationKeys.includes(key)) {
        properties.isIndividual = !props.value.isCompany;
      }
      try {
        existingClient.value = await ClientApi.clientsSearch(properties);
        if (existingClient.value?.id && props.fieldsApiValidation) {
          props.fieldsApiValidation[key] = String(existingClient.value?.id);
          emit('update:fieldsApiValidation', props.fieldsApiValidation);
        }
      } catch (e) {
        $errorHandler(e);
        return false;
      }
    }
  }
};

const validatePhoneNumberAndEmail = () => {
  findIfParamExists('email', props.value.email);
  findIfParamExists('phone', props.value.phone);
};
watch(() => props.value.isCompany, validatePhoneNumberAndEmail);

const agreements = computed<IAgreementItemViewModel[]>(() => {
  let agreementsList = AgreementViews.filter(agreement => agreement.key !== 'acceptsBigInquiry');
  if (!props.delegationGoal && !$user.value.isRealEstateUser) {
    agreementsList = agreementsList.filter(x => !x.isRealEstate);
  }
  if ($user.value.isRealEstateUser && !props.delegationGoal) {
    agreementsList = agreementsList.filter(x => x.productTypes.includes(ProductType.REAL_ESTATE));
  }
  if (props.delegationGoal) {
    agreementsList = agreementsList.filter(x => x.productTypes.includes(props.delegationGoal!));
  }

  agreementsList = agreementsList.filter(x => !x.isCompany);
  return agreementsList;
});

const requiredAgreements = computed<IAgreementItemViewModel[]>(() => {
  return agreements.value.filter(agreement => {
    return agreement.required(props.value.agreements) &&
      (!agreement.organisationIds || agreement.organisationIds.includes($user.value.organisationId!));
  });
});

const additionalAgreements = computed<IAgreementItemViewModel[]>(() => {
  return agreements.value.filter(agreement => {
    return !agreement.required(props.value.agreements) &&
      (!agreement.organisationIds || agreement.organisationIds.includes($user.value.organisationId!));
  });
});

const fieldChanged = () => {
  emit('update:dirty', true);
};

const showClientOwnerInfoCard = computed<boolean>(() => {
  return !!existingClient.value &&
        !!existingClient.value.owner &&
        existingClient.value.owner?.id !== AuthService.User?.id
});

const agreementChanged = (key: keyof ClientAgreements) => {
  fieldChanged();
  const existedChangedKeyId = keysChanged.value.indexOf(key);
  if (existedChangedKeyId > -1) {
    keysChanged.value.splice(existedChangedKeyId, 1);
  } else {
    keysChanged.value.push(key);
  }
  emit('update:agreement', {keysChanged: keysChanged.value, agreements: clientAgreements.value,});
};
const {$onboarding,} = useInstance()
const clientInfoRef = ref<ComponentPublicInstance | null>(null)
const fillAgreementsRef = ref<ComponentPublicInstance | null>(null)

onMounted(async() => {
  if (await $onboarding.tours.LEAD.isEnabled()) {
    useTour(clientInfoRef.value!, $onboarding.tours.LEAD.step.CLIENT_INFO);
    useTour(fillAgreementsRef.value!, $onboarding.tours.LEAD.step.FILL_AGREEMENTS)
  }
  props.value.mainAddress.landId = $user.value.landId;
  props.value.mainAddress.districtId = $user.value.districtId;
  if (props.isEdit) {
    clientCopy.value = clone(props.value);
  }
});

const openClientRodo = () => {
  $modalService.open(ClientRodo, {},
    {maxWidth: 650, persistent: true, fullscreen: undefined, });
};

const findRegon = async(nip: Nullable<string>) => {
  if (nip?.length === 10) {
    try {
      const response = await MultiformApi.getCompanyDataByNip(nip);
      if (response) {
        props.value.regon = response.regon;
      }
    } catch (e) {
      $snackbarService.openWarningSnackbar(I18NGetter().useEditClientForm.SNACKBAR_WARNING_REGON_NIP);
    }
  }
  fieldChanged();
};

const onCheckAllChange = () => {
  fieldChanged();
  agreementsChange(requiredAgreements.value);
  agreementsChange(additionalAgreements.value);
  emit('update:agreement', {keysChanged: keysChanged.value, agreements: clientAgreements.value,});
};

const agreementsChange = (agreements: Array<IAgreementItemViewModel>) => {
  agreements.forEach(agreement => {
    clientAgreements.value[agreement.key] = checkAll.value!;
    const existedChangedKeyId = keysChanged.value.indexOf(agreement.key);
    if (existedChangedKeyId > -1) {
      keysChanged.value.splice(existedChangedKeyId, 1);
    } else {
      keysChanged.value.push(agreement.key);
    }
  });
};

const showLink = computed(() => {
  // for domain == LENDI_PL show link
  // for NESTO_RO show only users with admin / verifier / coordinator permissions
  return ($env.isAppDomain().NESTO_RO && ($user.value.isAdminOrVerifier || $user.value.isCoordinator)) || $env.isAppDomain().LENDI_PL;
})

return { __sfc: true,props, $refs, $errorHandler, $user, $modalService, $snackbarService, $env, geoAreas, keysChanged, gpdrAgreement, clientAgreements, clientCopy, existingClient, clientOwner, checkAll, emit, fp_phone, phoneErrorMessage, validatePhone, findIfParamExists, validatePhoneNumberAndEmail, agreements, requiredAgreements, additionalAgreements, fieldChanged, showClientOwnerInfoCard, agreementChanged, $onboarding, clientInfoRef, fillAgreementsRef, openClientRodo, findRegon, onCheckAllChange, agreementsChange, showLink, ClientOwnerInfoCard, I18NGetter }
}

})